import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhoneIcon from "@mui/icons-material/Phone";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { getReq, delReq } from "../../configs/ApiUrl";
import DeleteWarningModal from "./util/DeleteWarningModal";
import AlwaysTop from "./util/AlwaysTop";
import defaultImg from "../../images/defaultImg.webp";
import locationMark from "../../images/locationMark.svg";
import { motion } from "framer-motion";
import DataTable from "../DataTable.js";

function ProductsHistory() {
  const { t } = useTranslation(["admin", "tables", "altText", "subadmin"]);
  const [loading, setLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [userRole, setUserRole] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [anchorEls, setAnchorEls] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    AlwaysTop();
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (userRole && userEmail) {
      getAllHistoryItems();
    }
  }, [userRole, userEmail]);

  const fetchUserProfile = async () => {
    try {
      const userProfileResp = await getReq("/user/profile");
      const userId = userProfileResp?.user?._id;
      const email = userProfileResp?.user?.email;
      setUserEmail(email);
      if (!userId) return;
      const roleResp = await getReq(`/user/${userId}`);
      if (roleResp?.success) setUserRole(roleResp.role);
    } catch (err) {
      console.error(err);
    }
  };

  const getAllHistoryItems = async () => {
    setLoading(true);
    try {
      const response = await getReq("/order/all");
      if (response?.success) {
        let filteredOrders = response.allOrders.filter((order) =>
          ["cancelled", "delivered"].includes(order.orderStatus)
        );
        if (userRole === "subadmin") {
          filteredOrders = filteredOrders.filter(
            (order) => order.subadminEmail === userEmail
          );
        }
        const sorted = filteredOrders.sort(
          (a, b) => new Date(a.orderDate) - new Date(b.orderDate)
        );
        setHistoryItems(sorted);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoadingStates((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await delReq(`/order/delete/${id}`);
      if (response?.success) await getAllHistoryItems();
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [id]: false }));
    }
  };

  const openDeleteModal = (id) => {
    setSelectedItemId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedItemId(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    if (selectedItemId) {
      await handleDelete(selectedItemId);
      closeDeleteModal();
    }
  };

  const handleMenuOpen = (event, id) => {
    setAnchorEls({ ...anchorEls, [id]: event.currentTarget });
  };

  const handleMenuClose = (id) => {
    setAnchorEls({ ...anchorEls, [id]: null });
  };

  const columns = [
    {
      header: "Title",
      accessor: (item) => {
        const title = item.orderItems[0]?.product?.title;
        return (
          <Tooltip
            title={title}
            placement='top'>
            <Typography variant='subtitle2'>
              {title?.length > 40 ? `${title.substring(0, 40)}...` : title}
            </Typography>
          </Tooltip>
        );
      },
      mobile: true,
      bold: true,
    },
    {
      header: "Details",
      accessor: (item) =>
        userRole === "admin" && (
          <Box
            display='flex'
            flexDirection='column'
            mb={1}>
            <Box
              display='flex'
              alignItems='center'
              gap={1}>
              <img
                src={locationMark}
                alt={t("altText:location") || "Location"}
                width={24}
              />
              <Typography variant='body2'>
                {`${item?.shippingAddress?.street}, ${item?.shippingAddress?.state}, ${item?.shippingAddress?.city}`}
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box
              display='flex'
              alignItems='center'
              gap={1}>
              <PhoneIcon
                fontSize='small'
                color='action'
              />
              <Typography>{item?.shippingAddress?.phone}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontWeight: "bold", color: "#000" }}>
              {item.orderItems[0]?.grandTotal ?? 0} kr
            </Typography>
          </Box>
        ),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className='w-full px-2'>
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        p={2}>
        {showDeleteModal && (
          <DeleteWarningModal
            open={showDeleteModal}
            onConfirm={handleConfirmDelete}
            onClose={closeDeleteModal}
          />
        )}
        <Paper sx={{ width: "100%", boxShadow: 3 }}>
          {loading ? (
            <Box
              display='flex'
              justifyContent='center'
              p={5}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              columns={columns}
              data={historyItems}
              getImage={(item) =>
                item.orderItems[0]?.product?.imageNames?.[0] || defaultImg
              }
              getRowKey={(item) => item._id}
              renderRowActions={(item) => (
                <>
                  <Tooltip title={t("deleteButton")}>
                    <IconButton
                      aria-label='delete'
                      onClick={() => openDeleteModal(item._id)}
                      disabled={loadingStates[item._id]}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              emptyMessage={t(
                userRole === "admin" ? "admin:noOrders" : "admin:noProducts"
              )}
              page={0}
              rowsPerPage={historyItems.length}
              handlePageChange={() => {}}
              handleRowsPerPageChange={() => {}}
            />
          )}
        </Paper>
      </Box>
    </motion.div>
  );
}

export default ProductsHistory;
